import React, { useState, useMemo, useEffect } from 'react';
import {
    WeeklyPlan, Subcontractor, Contract, Task, ProtocolState,
    SubcontractorEvaluationConfig, EvaluationParameter, GlobalUser, Responsible
} from '../../../types';
import { KlFullModal, KlDialog } from '../modal';
import {
    DEFAULT_EVALUATION_RANGES, EvaluationRangeBadge
} from '../../payments/EvaluationConfigModal';

interface MobileSubcontractorEvaluationModalProps {
    open: boolean;
    onClose: () => void;
    weeklyPlans: WeeklyPlan[];
    subcontractors: Subcontractor[];
    contracts?: Contract[];
    tasks?: Task[];
    savedProtocols?: Record<string, ProtocolState>;
    lookaheadPlans?: any[];
    evaluationConfig: SubcontractorEvaluationConfig;
    onOpenConfigModal?: () => void;
    hasEditPermission?: boolean;
    projectId?: string;
    initialPlanId?: number | null;
    globalUsers?: GlobalUser[];
    responsibles?: Responsible[];
}

export const MobileSubcontractorEvaluationModal: React.FC<MobileSubcontractorEvaluationModalProps> = ({
    open,
    onClose,
    weeklyPlans = [],
    subcontractors = [],
    contracts = [],
    tasks = [],
    savedProtocols = {},
    lookaheadPlans = [],
    evaluationConfig,
    onOpenConfigModal,
    hasEditPermission = true,
    projectId = 'default',
    initialPlanId = null,
    globalUsers = [],
    responsibles = []
}) => {
    // Semana seleccionada para matriz (null = vista listado semanas, number = matriz)
    const [selectedPlanId, setSelectedPlanId] = useState<number | null>(() => initialPlanId || null);

    useEffect(() => {
        if (open && initialPlanId !== undefined) {
            setSelectedPlanId(initialPlanId);
        }
    }, [open, initialPlanId]);

    // Buscador de semanas
    const [weekSearch, setWeekSearch] = useState('');

    // Storage key para evaluaciones
    const storageKey = `klydapp_sc_evaluations_${projectId}`;

    // Estado de Evaluaciones
    const [evaluations, setEvaluations] = useState<Record<string, Record<string, Record<string, number>>>>(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved) return JSON.parse(saved);
        } catch (e) {
            console.error('Error loading evaluations:', e);
        }
        return {};
    });

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [saveSuccessDialog, setSaveSuccessDialog] = useState(false);
    const [saveErrorDialog, setSaveErrorDialog] = useState(false);

    // Subcontratistas activos
    const activeSubcontractors = useMemo(() => {
        return subcontractors.filter(s => s.status !== 'Inactive');
    }, [subcontractors]);

    // Acordeón de subcontratistas en la matriz: primer subcontratista abierto por defecto
    const [expandedSubIds, setExpandedSubIds] = useState<Set<string>>(() => {
        return new Set(activeSubcontractors[0] ? [activeSubcontractors[0].id] : []);
    });

    const toggleSubExpand = (subId: string) => {
        setExpandedSubIds(prev => {
            const next = new Set(prev);
            if (next.has(subId)) next.delete(subId);
            else next.add(subId);
            return next;
        });
    };

    // Áreas y rangos
    const areas = useMemo(() => evaluationConfig?.areas || [], [evaluationConfig]);
    const ranges = useMemo(() => evaluationConfig?.ranges || DEFAULT_EVALUATION_RANGES, [evaluationConfig]);

    // Semana actualmente seleccionada
    const currentPlan = useMemo(() => {
        if (selectedPlanId === null) return null;
        return weeklyPlans.find(p => p.id === selectedPlanId) || null;
    }, [weeklyPlans, selectedPlanId]);

    // Usuarios del subcontratista
    const getSubcontractorUsers = (sub: Subcontractor) => {
        const subRazonNorm = (sub.razonSocial || '').trim().toLowerCase();
        const linkedSet = new Set(sub.linkedUserIds || []);
        const matchedMap = new Map<string, { id: string; name: string; email?: string }>();

        (globalUsers || []).forEach(u => {
            if (!u || u.status === 'Inactive') return;
            const uComp = (u.companyName || '').trim().toLowerCase();
            const matchesComp = Boolean(uComp && subRazonNorm && (uComp === subRazonNorm || uComp.includes(subRazonNorm) || subRazonNorm.includes(uComp)));
            const matchesLinked = linkedSet.has(u.id);
            if (matchesComp || matchesLinked) {
                matchedMap.set(u.id, { id: u.id, name: u.name, email: u.email });
            }
        });

        (responsibles || []).forEach(r => {
            if (!r) return;
            const rComp = (r.companyName || '').trim().toLowerCase();
            const matchesComp = Boolean(rComp && subRazonNorm && (rComp === subRazonNorm || rComp.includes(subRazonNorm) || subRazonNorm.includes(rComp)));
            const matchesLinked = linkedSet.has(r.id);
            const matchesSubId = r.id === sub.id;
            if (matchesComp || matchesLinked || matchesSubId) {
                if (!matchedMap.has(r.id)) {
                    matchedMap.set(r.id, { id: r.id, name: r.name, email: r.email });
                }
            }
        });

        return Array.from(matchedMap.values());
    };

    // Cálculo métricas automáticas
    const calculateAutoMetric = (param: EvaluationParameter, sub: Subcontractor, plan: WeeklyPlan): number | null => {
        const assignedUsers = getSubcontractorUsers(sub);
        const subContract = contracts.find(c => c.subcontractorId === sub.id);
        const subTaskIds = new Set<string>();
        if (subContract?.items) {
            subContract.items.forEach(it => { if (it.id) subTaskIds.add(it.id); });
        }
        tasks.forEach(t => {
            if (t.responsibleId === sub.id || (subContract?.items && subContract.items.some(it => it.id === t.id))) {
                subTaskIds.add(t.id);
            }
        });

        if (param.automaticCode === 'PPC') {
            const userPpcValues: number[] = [];
            assignedUsers.forEach(u => {
                const userAllocations = (plan.allocations || []).filter(a =>
                    a.responsibleId === u.id || (subTaskIds.has(a.taskId) && a.responsibleId === sub.id)
                );
                if (userAllocations.length > 0) {
                    const validAllocations = userAllocations.filter(a => a.executed !== null);
                    const listToCount = validAllocations.length > 0 ? validAllocations : userAllocations;
                    const executed = listToCount.filter(a => a.executed === true).length;
                    userPpcValues.push((executed / listToCount.length) * 100);
                }
            });

            if (userPpcValues.length > 0) {
                const sum = userPpcValues.reduce((acc, curr) => acc + curr, 0);
                return Math.round((sum / userPpcValues.length) * 10) / 10;
            }

            const subAllocations = (plan.allocations || []).filter(a =>
                a.responsibleId === sub.id || (subTaskIds.size > 0 && subTaskIds.has(a.taskId))
            );
            if (subAllocations.length > 0) {
                const validAllocations = subAllocations.filter(a => a.executed !== null);
                const listToCount = validAllocations.length > 0 ? validAllocations : subAllocations;
                const executed = listToCount.filter(a => a.executed === true).length;
                return Math.round(((executed / listToCount.length) * 100) * 10) / 10;
            }
            return null;
        }

        if (param.automaticCode === 'PCR') {
            const userPcrValues: number[] = [];
            const planStart = new Date(plan.startDate).getTime();
            const planEnd = new Date(plan.endDate).getTime();

            assignedUsers.forEach(u => {
                let userRestrictionsTotal = 0;
                let userRestrictionsReleased = 0;
                lookaheadPlans.forEach(lp => {
                    (lp.restrictions || []).forEach((r: any) => {
                        if (r.responsibleId === u.id && r.requiredDate) {
                            const reqTime = new Date(r.requiredDate).getTime();
                            if (reqTime >= planStart && reqTime <= planEnd) {
                                userRestrictionsTotal++;
                                if (r.status === 'Liberada' || r.isReleased) userRestrictionsReleased++;
                            }
                        }
                    });
                });
                if (userRestrictionsTotal > 0) {
                    userPcrValues.push((userRestrictionsReleased / userRestrictionsTotal) * 100);
                }
            });

            if (userPcrValues.length > 0) {
                const sum = userPcrValues.reduce((acc, curr) => acc + curr, 0);
                return Math.round((sum / userPcrValues.length) * 10) / 10;
            }
            return null;
        }

        if (param.automaticCode === 'CNC') {
            const subAllocations = (plan.allocations || []).filter(a =>
                a.responsibleId === sub.id || (subTaskIds.size > 0 && subTaskIds.has(a.taskId))
            );
            const nonCompliant = subAllocations.filter(a => a.executed === false);
            const cncCount = nonCompliant.length;
            if (cncCount === 0) return 100;
            if (cncCount <= 5) return 70;
            if (cncCount <= 10) return 50;
            return 10;
        }

        if (param.automaticCode === 'CONFIABILIDAD') {
            const relevantProtocols = Object.values(savedProtocols || {}).filter(p => {
                if (subTaskIds.size > 0 && subTaskIds.has(p.taskId)) return true;
                return (p as any).responsibleId === sub.id;
            });
            if (relevantProtocols.length === 0) return 100;
            const rejected = relevantProtocols.filter(p => p.status === 'Rechazado').length;
            return Math.max(0, Math.round((1 - (rejected / relevantProtocols.length)) * 100));
        }

        return null;
    };

    // Obtener puntaje de una celda
    const getScore = (planId: number, subId: string, param: EvaluationParameter, plan: WeeklyPlan): number | null => {
        if (param.indicatorType === 'Automatico') {
            return calculateAutoMetric(param, subcontractors.find(s => s.id === subId)!, plan);
        }
        const planKey = String(planId);
        const score = evaluations[planKey]?.[subId]?.[param.id];
        return score !== undefined ? score : 100;
    };

    // Actualizar puntaje manual
    const handleScoreChange = (planId: number, subId: string, paramId: string, value: number) => {
        const val = isNaN(value) ? 0 : Math.min(100, Math.max(0, value));
        const planKey = String(planId);
        setEvaluations(prev => ({
            ...prev,
            [planKey]: {
                ...(prev[planKey] || {}),
                [subId]: {
                    ...(prev[planKey]?.[subId] || {}),
                    [paramId]: val
                }
            }
        }));
        setHasUnsavedChanges(true);
    };

    // Guardar evaluaciones
    const handleSaveEvaluations = () => {
        try {
            localStorage.setItem(storageKey, JSON.stringify(evaluations));
            setHasUnsavedChanges(false);
            setSaveSuccessDialog(true);
        } catch (e) {
            console.error('Error saving evaluations:', e);
            setSaveErrorDialog(true);
        }
    };

    // Cálculo nota total de subcontratista
    const calculateSubcontractorTotalScore = (planId: number, subId: string, plan: WeeklyPlan): number | null => {
        let totalScore = 0;
        let validWeight = 0;

        areas.forEach(area => {
            const areaWeight = Number(area.weight) || 0;
            let areaScore = 0;
            let paramWeightSum = 0;

            area.parameters.forEach(param => {
                const paramWeight = Number(param.weight) || 0;
                const score = getScore(planId, subId, param, plan);
                if (score !== null) {
                    areaScore += (score * (paramWeight / 100));
                    paramWeightSum += paramWeight;
                }
            });

            if (paramWeightSum > 0) {
                const normalizedAreaScore = (areaScore / paramWeightSum) * 100;
                totalScore += (normalizedAreaScore * (areaWeight / 100));
                validWeight += areaWeight;
            }
        });

        if (validWeight === 0) return null;
        return Math.round(((totalScore / validWeight) * 100) * 10) / 10;
    };

    // Promedio general de la semana
    const calculateWeekAverage = (planId: number, plan: WeeklyPlan): number => {
        let sum = 0;
        let count = 0;
        activeSubcontractors.forEach(sub => {
            const score = calculateSubcontractorTotalScore(planId, sub.id, plan);
            if (score !== null) {
                sum += score;
                count++;
            }
        });
        return count > 0 ? Math.round((sum / count) * 10) / 10 : 0;
    };

    // Formato de fechas cortas
    const formatShortDateRange = (startStr?: string, endStr?: string) => {
        if (!startStr) return '';
        try {
            const parse = (s: string) => {
                const p = s.split('-');
                return p.length === 3 ? new Date(parseInt(p[0]), parseInt(p[1]) - 1, parseInt(p[2])) : new Date(s);
            };
            const d1 = parse(startStr);
            const d2 = endStr ? parse(endStr) : d1;
            const months = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
            if (d1.getFullYear() === d2.getFullYear()) {
                return `${d1.getDate()} ${months[d1.getMonth()]} – ${d2.getDate()} ${months[d2.getMonth()]} ${d1.getFullYear()}`;
            }
            return `${d1.getDate()} ${months[d1.getMonth()]} ${d1.getFullYear()} – ${d2.getDate()} ${months[d2.getMonth()]} ${d2.getFullYear()}`;
        } catch {
            return `${startStr} – ${endStr || ''}`;
        }
    };

    // Insignia de estado de Planificación
    const getPlanStatusBadge = (status?: string) => {
        const s = (status || '').toLowerCase();
        if (s.includes('ejecuc') || s === 'activa') {
            return (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-[10px] bg-[#DCFCE7] text-[#166534] dark:bg-emerald-950/40 dark:text-emerald-300">
                    En ejecución
                </span>
            );
        }
        if (s.includes('planif') || s === 'borrador') {
            return (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-[10px] bg-[#DBEAFE] text-[#1E40AF] dark:bg-blue-950/40 dark:text-blue-300">
                    En planificación
                </span>
            );
        }
        return (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-[10px] bg-[#F1F5F9] text-[#475569] dark:bg-slate-800 dark:text-slate-300">
                {status || 'Cerrada'}
            </span>
        );
    };

    const filteredPlans = useMemo(() => {
        return [...weeklyPlans]
            .filter(plan => {
                const q = weekSearch.toLowerCase();
                return (
                    String(plan.id).includes(q) ||
                    (plan.startDate && plan.startDate.includes(q)) ||
                    (plan.endDate && plan.endDate.includes(q)) ||
                    (plan.status && plan.status.toLowerCase().includes(q))
                );
            })
            .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
    }, [weeklyPlans, weekSearch]);

    return (
        <>
            {/* VENTANA 1: LISTADO DE SEMANAS (KLFULLMODAL) */}
            <KlFullModal
                open={open}
                onClose={onClose}
                title="Evaluación de Subcontratos (SC)"
                subtitle="Listado de semanas del proyecto"
                headerActions={
                    onOpenConfigModal && (
                        <button
                            type="button"
                            onClick={onOpenConfigModal}
                            className="w-10 h-10 rounded-full hover:bg-white/15 active:bg-white/25 flex items-center justify-center transition-colors text-white"
                            title="Configuración de Evaluación"
                        >
                            <span className="material-symbols-outlined text-[22px]">tune</span>
                        </button>
                    )
                }
            >
                <div className="flex flex-col">
                    {/* Fijo bajo la barra: el buscador de 40px y texto de ayuda a 11px */}
                    <div className="flex flex-col gap-1 p-3 bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 shrink-0">
                        <div className="relative w-full">
                            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">
                                search
                            </span>
                            <input
                                type="text"
                                placeholder="Buscar por fecha o número…"
                                value={weekSearch}
                                onChange={e => setWeekSearch(e.target.value)}
                                className="w-full h-10 pl-9 pr-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-[10px] text-[13px] text-slate-800 dark:text-slate-100 outline-none focus:border-[#2563EB]"
                            />
                        </div>
                        <span className="text-[11px] text-[#64748B] dark:text-slate-400 px-1">
                            Selecciona una semana para abrir su matriz y evaluar a los subcontratistas.
                        </span>
                    </div>

                    {/* Lista de Semanas: Cada semana es una fila .kl-row de 58px */}
                    <div className="flex flex-col divide-y divide-slate-100 dark:divide-slate-800 p-2">
                        {filteredPlans.length === 0 ? (
                            <div className="text-center py-12 text-slate-400 text-xs">
                                No se encontraron semanas de planificación.
                            </div>
                        ) : (
                            filteredPlans.map(plan => {
                                const isEvaluated = evaluations[String(plan.id)] !== undefined;
                                const weekAvg = calculateWeekAverage(plan.id, plan);

                                return (
                                    <div
                                        key={plan.id}
                                        onClick={() => setSelectedPlanId(plan.id)}
                                        className="kl-row min-h-[58px] py-2 px-3 flex items-center justify-between gap-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer active:bg-slate-100 transition-colors"
                                    >
                                        {/* Izquierda: Cuadro tonal de 32px + Número */}
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <div className="w-8 h-8 rounded-[10px] bg-[#EFF6FF] dark:bg-blue-950/50 text-[#2563EB] dark:text-blue-300 font-mono font-bold text-[12px] flex items-center justify-center shrink-0">
                                                #{plan.id}
                                            </div>

                                            <div className="flex flex-col min-w-0">
                                                {/* Primera línea: "Semana 16" a 14px/700 y la insignia de estado */}
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[14px] font-bold text-[#0F172A] dark:text-slate-100">
                                                        Semana {plan.id}
                                                    </span>
                                                    {getPlanStatusBadge(plan.status)}
                                                </div>

                                                {/* Segunda línea: fechas en formato corto, "12 oct – 19 oct 2026", a 12px gris */}
                                                <span className="text-[12px] text-[#64748B] dark:text-slate-400 mt-0.5">
                                                    {formatShortDateRange(plan.startDate, plan.endDate)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Derecha: evaluación general en pastilla ("Pendiente" o la nota) y un chevron */}
                                        <div className="flex items-center gap-2 shrink-0">
                                            {isEvaluated ? (
                                                <span className="text-[11px] font-bold px-2.5 py-1 rounded-[10px] bg-[#DCFCE7] text-[#166534] dark:bg-emerald-950/40 dark:text-emerald-300 font-mono">
                                                    {weekAvg}%
                                                </span>
                                            ) : (
                                                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-[10px] bg-slate-100 text-[#64748B] dark:bg-slate-800 dark:text-slate-400 italic">
                                                    Pendiente
                                                </span>
                                            )}
                                            <span className="material-symbols-outlined text-[18px] text-slate-400">
                                                chevron_right
                                            </span>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </KlFullModal>

            {/* VENTANA 2: MATRIZ DE EVALUACIÓN DE UNA SEMANA (KLFULLMODAL APILADA) */}
            {selectedPlanId !== null && (
                <KlFullModal
                    open={selectedPlanId !== null}
                    onClose={() => setSelectedPlanId(null)}
                    title={`Evaluación Semana #${selectedPlanId}`}
                    subtitle={formatShortDateRange(currentPlan?.startDate, currentPlan?.endDate)}
                    footer={
                        hasEditPermission && (
                            <button
                                type="button"
                                onClick={handleSaveEvaluations}
                                className="b b-p w-full h-11 rounded-[10px] text-[13px] font-bold text-center flex items-center justify-center gap-2"
                            >
                                <span className="material-symbols-outlined text-[18px]">save</span>
                                <span>Guardar evaluación</span>
                            </button>
                        )
                    }
                >
                    <div className="flex flex-col gap-3 p-3 pb-8">
                        {activeSubcontractors.length === 0 ? (
                            <div className="card text-center py-12 text-slate-400 text-xs">
                                No hay empresas contratistas registradas para evaluar.
                            </div>
                        ) : areas.length === 0 ? (
                            <div className="card text-center py-12 text-slate-400 text-xs">
                                No hay áreas de evaluación configuradas.
                            </div>
                        ) : (
                            activeSubcontractors.map(sub => {
                                const isExpanded = expandedSubIds.has(sub.id);
                                const finalScore = currentPlan ? calculateSubcontractorTotalScore(selectedPlanId, sub.id, currentPlan) : null;

                                return (
                                    <div
                                        key={sub.id}
                                        className="card bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex flex-col gap-3"
                                    >
                                        {/* Encabezado Acordeón Subcontratista: Nombre y Nota Total */}
                                        <div
                                            onClick={() => toggleSubExpand(sub.id)}
                                            className="flex items-center justify-between gap-2 cursor-pointer select-none"
                                        >
                                            <div className="flex flex-col min-w-0 flex-1">
                                                <h4 className="text-[14px] font-bold text-[#0F172A] dark:text-slate-100 leading-snug line-clamp-2">
                                                    {sub.razonSocial}
                                                </h4>
                                                <span className="font-mono text-[11px] text-[#64748B] dark:text-slate-400 mt-0.5">
                                                    RUT: {sub.rut || 'S/RUT'}
                                                </span>
                                            </div>

                                            <div className="flex items-center gap-2 shrink-0">
                                                {/* Pastilla con Nota Total */}
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-[10px] font-bold text-[#64748B] uppercase">
                                                        Nota:
                                                    </span>
                                                    <span className={`text-[12px] font-mono font-black px-2 py-0.5 rounded-[10px] ${
                                                        finalScore !== null
                                                            ? 'bg-[#DCFCE7] text-[#166534] dark:bg-emerald-950/40 dark:text-emerald-300'
                                                            : 'bg-slate-100 text-slate-500 dark:bg-slate-800'
                                                    }`}>
                                                        {finalScore !== null ? `${finalScore}%` : 'N/A'}
                                                    </span>
                                                </div>

                                                <span className="material-symbols-outlined text-[20px] text-slate-400">
                                                    {isExpanded ? 'expand_less' : 'expand_more'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Desglose de Parámetros por fila */}
                                        {isExpanded && (
                                            <div className="flex flex-col gap-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                                                {areas.map(area => (
                                                    <div key={area.id} className="flex flex-col gap-2">
                                                        {/* Título de Área */}
                                                        <div className="flex items-center justify-between px-1">
                                                            <span className="text-[11px] font-bold uppercase tracking-wider text-[#64748B] dark:text-slate-400">
                                                                {area.name} ({area.weight}%)
                                                            </span>
                                                        </div>

                                                        {/* Parámetros */}
                                                        <div className="flex flex-col gap-2">
                                                            {area.parameters.map(param => {
                                                                const cellScore = currentPlan ? getScore(selectedPlanId, sub.id, param, currentPlan) : null;

                                                                return (
                                                                    <div
                                                                        key={param.id}
                                                                        className="p-2.5 rounded-[10px] bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800 flex flex-col gap-1.5"
                                                                    >
                                                                        {/* Nombre arriba con código monoespaciado */}
                                                                        <div className="flex items-baseline justify-between gap-2">
                                                                            <div className="flex items-baseline gap-1.5 flex-1 min-w-0">
                                                                                <span className="font-mono text-[11px] font-bold text-[#64748B] dark:text-slate-400 shrink-0">
                                                                                    {area.correlative}.{param.correlative}
                                                                                </span>
                                                                                <span className="text-[13px] font-medium text-[#0F172A] dark:text-slate-200 leading-snug line-clamp-2">
                                                                                    {param.name}
                                                                                </span>
                                                                            </div>
                                                                            <span className="text-[10px] font-bold text-[#64748B] shrink-0">
                                                                                Pond: {param.weight}%
                                                                            </span>
                                                                        </div>

                                                                        {/* Campo o selector de nota debajo a ancho completo */}
                                                                        {param.indicatorType === 'Manual' ? (
                                                                            <div className="relative w-full">
                                                                                <input
                                                                                    type="number"
                                                                                    min="0"
                                                                                    max="100"
                                                                                    value={cellScore ?? 100}
                                                                                    onChange={e => handleScoreChange(selectedPlanId, sub.id, param.id, parseFloat(e.target.value))}
                                                                                    disabled={!hasEditPermission}
                                                                                    className="w-full h-10 px-3 pr-8 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-[10px] text-[13px] font-mono font-bold text-[#0F172A] dark:text-slate-100 outline-none focus:border-[#2563EB]"
                                                                                />
                                                                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-slate-400 pointer-events-none">
                                                                                    %
                                                                                </span>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="w-full h-10 px-3 rounded-[10px] bg-purple-50 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-900/50 flex items-center justify-between">
                                                                                <div className="flex items-center gap-1.5">
                                                                                    <span className="material-symbols-outlined text-[16px] text-purple-600 dark:text-purple-400">
                                                                                        auto_awesome
                                                                                    </span>
                                                                                    <span className="text-[11px] font-bold text-purple-900 dark:text-purple-200">
                                                                                        Auto: {param.automaticCode}
                                                                                    </span>
                                                                                </div>
                                                                                <span className="font-mono font-extrabold text-[13px] text-purple-900 dark:text-purple-200">
                                                                                    {cellScore !== null ? `${cellScore}%` : 'N/A'}
                                                                                </span>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </KlFullModal>
            )}

            {/* Diálogo de Éxito al Guardar */}
            <KlDialog
                open={saveSuccessDialog}
                onClose={() => setSaveSuccessDialog(false)}
                title="Evaluación guardada"
                message="Las calificaciones de la semana han sido guardadas exitosamente."
                confirmText="Aceptar"
                cancelText={null}
                onConfirm={() => setSaveSuccessDialog(false)}
            />

            {/* Diálogo de Error al Guardar */}
            <KlDialog
                open={saveErrorDialog}
                onClose={() => setSaveErrorDialog(false)}
                title="Error al guardar"
                message="No se pudieron guardar las evaluaciones. Por favor intenta nuevamente."
                confirmText="Entendido"
                cancelText={null}
                danger={true}
                onConfirm={() => setSaveErrorDialog(false)}
            />
        </>
    );
};
